/**
 * Availability Message Component
 * Handles dynamic updating of availability messages when product variants change
 */

class AvailabilityMessage {
  constructor(element) {
    this.element = element;
    this.productHandle = element.dataset.productHandle;
    this.container = element.closest('[data-product]') || element.closest('product-component');
    this.isLoading = false;
    this.abortController = null;
    
    this.init();
  }

  init() {
    if (!this.container) return;
    
    // Listen for variant changes
    this.container.addEventListener('theme:variant:change', (event) => {
      this.handleVariantChange(event);
    });
  }

  handleVariantChange(event) {
    console.log('AvailabilityMessage: handleVariantChange called', event.detail);
    
    const variant = event.detail.variant;
    
    if (!variant) {
      console.log('AvailabilityMessage: No variant provided, clearing message');
      this.clearAvailabilityMessage();
      return;
    }

    console.log('AvailabilityMessage: Updating availability for variant', variant.id);
    this.updateAvailabilityMessage(variant.id);
  }

  async updateAvailabilityMessage(variantId) {
    console.log('AvailabilityMessage: updateAvailabilityMessage called for variant', variantId);
    
    if (this.isLoading) {
      console.log('AvailabilityMessage: Already loading, aborting previous request');
      this.abortController?.abort();
    }

    this.isLoading = true;
    this.abortController = new AbortController();

    try {
      this.showLoadingState();
      
      // Skip the section approach and go directly to fallback
      console.log('AvailabilityMessage: Using fallback method for better reliability');
      await this.updateAvailabilityMessageFallback(variantId);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('AvailabilityMessage: Error updating availability message:', error);
      }
    } finally {
      this.hideLoadingState();
      this.isLoading = false;
      this.abortController = null;
    }
  }

  async updateAvailabilityMessageFallback(variantId) {
    console.log('AvailabilityMessage: updateAvailabilityMessageFallback called for variant', variantId);
    
    try {
      const url = `/products/${this.productHandle}?variant=${variantId}`;
      console.log('AvailabilityMessage: Fetching', url);
      
      // Fetch the full product page with the variant parameter
      const response = await fetch(url, {
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        throw new Error(`Fallback HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      console.log('AvailabilityMessage: Received response, length:', html.length);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for the availability message in the full page
      const availabilityElement = doc.querySelector('[data-availability-message]');
      
      if (availabilityElement) {
        const oldContent = this.element.innerHTML;
        const newContent = availabilityElement.innerHTML;
        
        // Extract just the inner content (not the wrapper with data attributes)
        this.element.innerHTML = newContent;
        this.element.dataset.currentVariantId = variantId;
        
        console.log('AvailabilityMessage: Successfully updated content');
        console.log('AvailabilityMessage: Old content:', oldContent.substring(0, 100));
        console.log('AvailabilityMessage: New content:', newContent.substring(0, 100));
      } else {
        console.warn('AvailabilityMessage: Could not find availability message in fallback response');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('AvailabilityMessage: Fallback method failed:', error);
      }
    }
  }

  showLoadingState() {
    this.element.style.opacity = '0.5';
    this.element.style.pointerEvents = 'none';
    this.element.setAttribute('aria-busy', 'true');
  }

  hideLoadingState() {
    this.element.style.opacity = '';
    this.element.style.pointerEvents = '';
    this.element.removeAttribute('aria-busy');
  }

  clearAvailabilityMessage() {
    this.element.innerHTML = '';
  }
}

// Initialize availability message components when DOM is ready
function initializeAvailabilityMessages() {
  const availabilityElements = document.querySelectorAll('[data-availability-message]');
  
  console.log('Initializing availability messages for', availabilityElements.length, 'elements');
  
  availabilityElements.forEach((element, index) => {
    // Avoid double initialization
    if (element.dataset.availabilityInitialized) {
      console.log('Element', index, 'already initialized, skipping');
      return;
    }
    
    try {
      const instance = new AvailabilityMessage(element);
      element.dataset.availabilityInitialized = 'true';
      
      // Store instance on element for debugging
      element._availabilityInstance = instance;
      
      console.log('Successfully initialized availability message for element', index);
    } catch (error) {
      console.error('Failed to initialize availability message for element', index, ':', error);
    }
  });
}

// Try multiple initialization strategies
document.addEventListener('DOMContentLoaded', initializeAvailabilityMessages);

// Also try when the window loads (fallback)
window.addEventListener('load', () => {
  setTimeout(initializeAvailabilityMessages, 100);
});

// Also initialize immediately if DOM is already ready
if (document.readyState === 'loading') {
  // DOM is still loading
} else {
  // DOM is already loaded
  setTimeout(initializeAvailabilityMessages, 100);
}

// Export for manual initialization if needed
window.AvailabilityMessage = AvailabilityMessage;